// ============================================================================
// crmAvisoComputador · a inscrição do computador da recepção no aviso de mensagem (30/10/2026)
// ============================================================================
// Pedido do dono (30/10/2026): "Preciso fazer notificação de mensagem para quando
// a recepcionista estiver no computador e estiver fora do DNA, por exemplo no Avec,
// ou então para quando ela estiver em outra aba do DNA e não tiver no CRM. Como não
// está chegando nenhuma notificação nem nada, elas estão se perdendo."
// O computador da recepção nunca se inscrevia no push (o NotificationsGate só age no
// app instalado no celular e a chave da Aparência só pedia a permissão). Aqui:
//   · ativarAvisosComputador(user): SÓ dentro do clique (Safari e Chrome exigem o
//     gesto): pede a permissão, inscreve pelo subscribeToPush de sempre e grava a
//     marca CHAVE_PUSH_COMPUTADOR { id, em } deste navegador;
//   · reinscreverSeConcedido(user, { forcar }): em silêncio, nunca pede permissão. Com a
//     permissão já dada e a marca deste navegador, refaz a inscrição quando ela sumiu (a
//     recuperação de tela branca desregistra o service worker, main.jsx e index.html),
//     quando a marca é de outra pessoa (computador do balcão compartilhado) ou uma
//     vez por dia (renova o vínculo no servidor); forcar: true refaz mesmo com tudo em dia
//     (o PUSH_SUBSCRIPTION_CHANGED do service worker: o navegador trocou o endpoint);
//   · avisosComputadorAtivos(): síncrono, para o vigia decidir se o aviso da página
//     é necessário (com o push ativo, quem avisa é o sistema). Se a reinscrição desta
//     página achou a inscrição perdida e não conseguiu refazer, volta a ser false (o
//     aviso da página não some à toa).
// 30/10/2026 (revisão): a inscrição do computador é SEPARADA do push pessoal. Tudo aqui
// inscreve e desinscreve com { destino: DESTINO_PUSH_COMPUTADOR } (crmAvisoContrato.js): o
// servidor grava a linha como 'crm-pc:<id da sessão>', o push pessoal (duelo, aniversário,
// fila) não chega neste computador por ela e o aviso de mensagem só vai para quem ativou.
// Nada lança para a tela: todo erro vira { ok: false, motivo }.
// Ponto de volta: CRM_VIGIA = false em src/config/featureFlags.js e tudo fica como antes (sem
// faixa, a chave da Aparência só pede a permissão, ninguém se inscreve).
// ============================================================================

import { subscribeToPush, unsubscribeFromPush } from './pushNotifications.js';
import { CRM_VIGIA } from '../config/featureFlags.js';
import {
  eComputador, CHAVE_PUSH_COMPUTADOR, TITULO_AVISO_MENSAGEM, TIPO_AVISO_MENSAGEM, urlAvisoMensagem,
  DESTINO_PUSH_COMPUTADOR, TAG_AVISO_TESTE,
} from './crmAvisoContrato.js';

// 30/10/2026 · o interruptor desta parte segue o do vigia (revisão: quem responde ao pedido de som do
// service worker é o vigia; o push do computador sem ele tocaria o som do sistema junto com o da Caixa,
// então um interruptor só para as duas metades). true = a faixa "Ativar avisos" no alto do CRM, a chave
// da Aparência que inscreve o computador e o "Testar aviso", a reinscrição silenciosa. false = exatamente
// como antes.
export const AVISO_COMPUTADOR = CRM_VIGIA === true;

/** "Agora não" da faixa: a data local (AAAA-MM-DD) do dia em que ela fica escondida. */
export const CHAVE_FAIXA_ADIADA = 'fk.crm.avisoComputador.adiada';
/** A tag do aviso de teste da Aparência (o service worker trata como aviso de mensagem): a do contrato. */
export { TAG_AVISO_TESTE };
/** A marca deste navegador mais velha que isto renova o vínculo no servidor (1 POST por dia). */
export const RENOVAR_MARCA_MS = 24 * 3_600_000;
/** Teto da inscrição (o service worker pode ainda não estar pronto depois da recuperação). */
export const LIMITE_INSCRICAO_MS = 20_000;

/** Os textos que a equipe lê (faixa e Aparência), num lugar só. Sem travessão. */
export const TEXTOS_AVISO = Object.freeze({
  convite: 'Receba aviso de mensagem nova neste computador, mesmo com o Avec aberto.',
  bloqueado: 'Os avisos estão bloqueados neste navegador. No Chrome: clique no cadeado ao lado do endereço, depois em Notificações e Permitir. No Safari: Ajustes, Sites, Notificações, feelking.app, Permitir.',
  semSuporte: 'Este navegador não recebe avisos. Use o Chrome ou o Safari atualizado.',
  falhou: 'Não deu para ativar os avisos agora.',
  ligados: 'Avisos ligados neste computador.',
  desligarFalhou: 'Não deu para desligar os avisos deste computador agora. Tente de novo.',
  privacidade: 'Mostra só a unidade, nunca o nome nem o texto da mensagem.',
  testeCorpo: 'Teste: os avisos de mensagem chegam neste computador.',
  testeEnviado: 'Aviso de teste enviado. Se não apareceu, libere as notificações do navegador nos ajustes do sistema.',
  testeFalhou: 'Não deu para mostrar o aviso de teste agora.',
});

// ── o ambiente (os testes passam um falso; no navegador são os de verdade) ──
function guardado(fn) { try { return fn() || null; } catch { return null; } }
export function ambiente() {
  const w = typeof window !== 'undefined' ? window : undefined;
  const nav = typeof navigator !== 'undefined' ? navigator : (w && w.navigator) || undefined;
  return { window: w, navigator: nav, localStorage: guardado(() => w && w.localStorage), sessionStorage: guardado(() => w && w.sessionStorage) };
}

/** Notification, service worker e PushManager (Safari antigo e iPad numa aba ficam de fora). */
export function suportado(amb = ambiente()) {
  try {
    const w = amb && amb.window;
    const nav = amb && amb.navigator;
    return !!w && !!nav && 'Notification' in w && 'PushManager' in w && 'serviceWorker' in nav && !!nav.serviceWorker;
  } catch { return false; }
}

/** É computador (o aviso de mensagem vai só para eles, não para o iPhone pessoal). */
export function eComputadorDaqui(amb = ambiente()) {
  try { return eComputador(amb && amb.navigator && amb.navigator.userAgent); } catch { return false; }
}

/** 'default' | 'granted' | 'denied' | 'sem_suporte'. */
export function permissao(amb = ambiente()) {
  if (!suportado(amb)) return 'sem_suporte';
  try {
    const p = amb.window.Notification.permission;
    return p === 'granted' || p === 'denied' ? p : 'default';
  } catch { return 'sem_suporte'; }
}

/** A chave do destinatário do push, a mesma do NotificationsGate: o perfil, senão o id do usuário. */
export function idDestinatario(user) {
  const id = user && (user.professionalId || user.id);
  return id ? String(id) : null;
}

/**
 * Quem vê a faixa "Ativar avisos" (30/10/2026, revisão): só quem RECEBE o aviso pelo servidor sem ser dona
 * da conversa (api/_lib/crm-sofia-aviso-mensagem.js): a Recepcionista (a unidade dela e CRM_REDE_RECEPCAO)
 * e o master (ativa no computador dele para testar). A Gerente recebe só como dona da conversa: sem faixa
 * (a chave da Aparência continua lá para quem quiser). acesso = o da tela (acessoDaTela do op me).
 */
export function mostrarFaixaPara(acesso) {
  return !!acesso && typeof acesso === 'object' && (acesso.master === true || acesso.cargo === 'Recepcionista');
}

/** Quem está logado neste navegador (o AuthContext guarda a sessão em fk_session). */
export function usuarioDaSessao(amb = ambiente()) {
  try {
    const bruto = amb && amb.sessionStorage ? amb.sessionStorage.getItem('fk_session') : null;
    const s = bruto ? JSON.parse(bruto) : null;
    return s && typeof s === 'object' ? { id: s.id || null, professionalId: s.professionalId || null } : null;
  } catch { return null; }
}

/** A marca { id, em } de quem inscreveu este navegador; estranha ou ausente: null. */
export function lerMarca(amb = ambiente()) {
  try {
    const bruto = amb && amb.localStorage ? amb.localStorage.getItem(CHAVE_PUSH_COMPUTADOR) : null;
    const m = bruto ? JSON.parse(bruto) : null;
    return m && typeof m === 'object' && typeof m.id === 'string' && m.id ? { id: m.id, em: typeof m.em === 'string' ? m.em : null } : null;
  } catch { return null; }
}

function gravarMarca(id, amb, agoraMs) {
  try { amb.localStorage.setItem(CHAVE_PUSH_COMPUTADOR, JSON.stringify({ id, em: new Date(agoraMs).toISOString() })); return true; } catch { return false; }
}

function apagarMarca(amb) {
  try { amb.localStorage.removeItem(CHAVE_PUSH_COMPUTADOR); } catch { /* sem armazenamento: nada a apagar */ }
}

// a última conferência desta página (por navegador): a inscrição sumiu e não voltou = o push não chega,
// então os avisos NÃO contam como ativos e o vigia volta ao aviso da página (some ao recarregar)
const conferidos = new WeakMap();
function anotar(amb, ok) {
  try { if (amb && amb.navigator && typeof amb.navigator === 'object') conferidos.set(amb.navigator, ok); } catch { /* sem anotação: vale a marca */ }
}

/**
 * Os avisos do computador estão ativos para quem está logado (síncrono; sem user, lê a sessão): a permissão
 * dada, a marca deste navegador com o id de quem está logado e nenhuma conferência desta página que achou
 * a inscrição perdida sem conseguir refazer.
 */
export function avisosComputadorAtivos(user, amb = ambiente()) {
  if (!AVISO_COMPUTADOR) return false;
  try {
    if (!eComputadorDaqui(amb) || permissao(amb) !== 'granted') return false;
    const id = idDestinatario(user || usuarioDaSessao(amb));
    const m = id ? lerMarca(amb) : null;
    return !!m && m.id === id && conferidos.get(amb.navigator) !== false;
  } catch { return false; }
}

/** A promessa com teto (o .ready do service worker nunca resolve sem um registrado). */
function comLimite(promessa, ms) {
  let t = null;
  const teto = new Promise((_, nao) => { t = setTimeout(() => nao(new Error('tempo esgotado')), ms); });
  return Promise.race([Promise.resolve(promessa), teto]).finally(() => clearTimeout(t));
}

/** O registro do service worker sem esperar (undefined depois da recuperação de tela branca). */
async function registroAgora(amb) {
  const sw = amb.navigator.serviceWorker;
  return typeof sw.getRegistration === 'function' ? sw.getRegistration() : undefined;
}

/** Este navegador tem inscrição de push? { ok, inscrito } (nunca lança). */
export async function conferirInscricao(amb = ambiente()) {
  if (!suportado(amb)) return { ok: false, motivo: 'sem_suporte', inscrito: false };
  try {
    const reg = await comLimite(registroAgora(amb), 5_000);
    if (!reg || !reg.pushManager) return { ok: true, inscrito: false, semServiceWorker: !reg };
    const sub = await reg.pushManager.getSubscription();
    return { ok: true, inscrito: !!sub };
  } catch { return { ok: false, motivo: 'falhou', inscrito: false }; }
}

/** O pedido de permissão nas duas formas (promessa e o callback do Safari antigo). */
function pedirPermissao(N) {
  return new Promise((resolver) => {
    let feito = false;
    const fim = (p) => { if (!feito) { feito = true; resolver(p || N.permission); } };
    try {
      const r = N.requestPermission(fim);
      if (r && typeof r.then === 'function') r.then(fim, () => fim(N.permission));
    } catch { fim(N.permission); }
  });
}

/**
 * O botão "Ativar avisos" e a chave da Aparência: chamar DENTRO do clique, sem nada assíncrono antes
 * (o Safari só mostra o pedido de permissão no gesto). Devolve { ok, motivo }.
 * motivo: 'desligado' | 'sem_suporte' | 'nao_computador' | 'sem_sessao' | 'denied' | 'default' (fechou sem escolher) | 'falhou'.
 * inscrever(id, { destino }) = o subscribeToPush de sempre com o destino do computador.
 */
export async function ativarAvisosComputador(user, { amb = ambiente(), inscrever = subscribeToPush, agoraMs = null, limiteMs = LIMITE_INSCRICAO_MS } = {}) {
  try {
    if (!AVISO_COMPUTADOR) return { ok: false, motivo: 'desligado' };
    if (!suportado(amb)) return { ok: false, motivo: 'sem_suporte' };
    if (!eComputadorDaqui(amb)) return { ok: false, motivo: 'nao_computador' };
    const id = idDestinatario(user);
    if (!id) return { ok: false, motivo: 'sem_sessao' };
    const N = amb.window.Notification;
    let p = N.permission;
    if (p === 'default') p = await pedirPermissao(N);
    if (p === 'denied') return { ok: false, motivo: 'denied' };
    if (p !== 'granted') return { ok: false, motivo: 'default' };
    await comLimite(inscrever(id, { destino: DESTINO_PUSH_COMPUTADOR }), limiteMs);
    gravarMarca(id, amb, agoraMs ?? Date.now());
    anotar(amb, true);
    return { ok: true, motivo: null };
  } catch (e) {
    return { ok: false, motivo: 'falhou', erro: String((e && e.message) || e || 'falhou') };
  }
}

let reinscrevendo = null;

/**
 * Em silêncio (o vigia e a faixa chamam ao montar): NUNCA pede permissão. Só no computador, com a
 * permissão já dada e a marca deste navegador: refaz a inscrição quando ela sumiu, quando a marca é
 * de outra pessoa (o balcão compartilhado: o push passa para quem está logado) ou quando a marca tem
 * mais de 1 dia. Sem marca: nada (a pessoa nunca ativou aqui; a faixa convida). Nunca lança.
 * forcar: true (30/10/2026, o PUSH_SUBSCRIPTION_CHANGED do service worker, que o vigia escuta): refaz
 * mesmo com a inscrição de pé e a marca nova (o navegador trocou o endpoint e o servidor só conhece o
 * velho). Continua exigindo a marca: sem ela a troca é do push pessoal e a linha não vira 'crm-pc'.
 * Duas chamadas juntas inscrevem uma vez; a forçada que chega com outra em voo espera e roda depois
 * (a em voo pode ter conferido antes da troca).
 */
export function reinscreverSeConcedido(user, opcoes = {}) {
  const forcar = !!(opcoes && opcoes.forcar === true);
  if (reinscrevendo && !forcar) return reinscrevendo;
  const anterior = reinscrevendo;
  const seguir = () => reinscrever(user, opcoes);
  const atual = (anterior ? anterior.then(seguir, seguir) : seguir())
    .finally(() => { if (reinscrevendo === atual) reinscrevendo = null; });
  reinscrevendo = atual;
  return atual;
}

async function reinscrever(user, { amb = ambiente(), inscrever = subscribeToPush, agoraMs = null, limiteMs = LIMITE_INSCRICAO_MS, forcar = false } = {}) {
  try {
    if (!AVISO_COMPUTADOR) return { ok: false, motivo: 'desligado' };
    if (!suportado(amb)) return { ok: false, motivo: 'sem_suporte' };
    if (!eComputadorDaqui(amb)) return { ok: false, motivo: 'nao_computador' };
    const p = permissao(amb);
    if (p !== 'granted') return { ok: false, motivo: p };
    const id = idDestinatario(user || usuarioDaSessao(amb));
    if (!id) return { ok: false, motivo: 'sem_sessao' };
    const marca = lerMarca(amb);
    if (!marca) return { ok: true, feito: false, motivo: 'sem_marca' };
    const agora = agoraMs ?? Date.now();
    const insc = await conferirInscricao(amb);
    const deOutra = marca.id !== id;
    const velha = !(Date.parse(marca.em || '') > agora - RENOVAR_MARCA_MS);
    if (forcar !== true && insc.ok && insc.inscrito && !deOutra && !velha) { anotar(amb, true); return { ok: true, feito: false, motivo: null }; }
    try {
      await comLimite(inscrever(id, { destino: DESTINO_PUSH_COMPUTADOR }), limiteMs);
    } catch (e) {
      // a inscrição perdida que não voltou: o push não chega (renovar falhando com a inscrição de pé, sim)
      if (!(insc.ok && insc.inscrito)) anotar(amb, false);
      throw e;
    }
    gravarMarca(id, amb, agora);
    anotar(amb, true);
    return { ok: true, feito: true, motivo: deOutra ? 'outra_pessoa' : !insc.inscrito ? 'sem_inscricao' : velha ? 'renovada' : 'forcada' };
  } catch (e) {
    return { ok: false, motivo: 'falhou', erro: String((e && e.message) || e || 'falhou') };
  }
}

/**
 * A chave da Aparência desligada: sai SÓ o aviso do CRM deste computador (a linha 'crm-pc', pelo destino;
 * o push pessoal fica, 30/10/2026, revisão) e a marca some. A permissão do navegador fica como está.
 * desinscrever({ destino }) = o unsubscribeFromPush de sempre com o destino do computador (sem a confirmação
 * do servidor, ele tira a inscrição do navegador: a chave desligada não deixa o aviso chegando).
 * A marca só some com a saída feita: na falha ela fica, a chave continua ligada (é a verdade: o aviso
 * ainda chega) e a tela diz que não deu. Nunca lança.
 */
export async function desligarAvisosComputador({ amb = ambiente(), desinscrever = unsubscribeFromPush, limiteMs = LIMITE_INSCRICAO_MS } = {}) {
  if (!suportado(amb)) { apagarMarca(amb); return { ok: true }; }
  try {
    await comLimite(desinscrever({ destino: DESTINO_PUSH_COMPUTADOR }), limiteMs);
    apagarMarca(amb);
    return { ok: true };
  } catch { return { ok: false, motivo: 'falhou' }; }
}

/** O "Testar aviso" da Aparência: um aviso de teste pelo service worker (permissão e som do sistema). */
export async function testarAvisoComputador({ amb = ambiente() } = {}) {
  const p = permissao(amb);
  if (p !== 'granted') return { ok: false, motivo: p };
  const opcoes = {
    body: TEXTOS_AVISO.testeCorpo, tag: TAG_AVISO_TESTE, renotify: true, icon: '/pwa-192x192.png', badge: '/pwa-64x64.png',
    data: { tipo: TIPO_AVISO_MENSAGEM, url: urlAvisoMensagem(null), teste: true },
  };
  try {
    const reg = await comLimite(registroAgora(amb), 5_000);
    if (reg && typeof reg.showNotification === 'function') { await reg.showNotification(TITULO_AVISO_MENSAGEM, opcoes); return { ok: true, via: 'service_worker' }; }
    // sem service worker (a prévia local): o aviso direto da página, para ao menos conferir a permissão
    new amb.window.Notification(TITULO_AVISO_MENSAGEM, { body: opcoes.body, tag: opcoes.tag });
    return { ok: true, via: 'pagina' };
  } catch { return { ok: false, motivo: 'falhou' }; }
}

// ── "Agora não" da faixa: some até o dia seguinte (neste navegador) ──
function diaLocal(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function faixaAdiada(agoraMs = Date.now(), amb = ambiente()) {
  try { return !!amb.localStorage && amb.localStorage.getItem(CHAVE_FAIXA_ADIADA) === diaLocal(agoraMs); } catch { return false; }
}
export function adiarFaixa(agoraMs = Date.now(), amb = ambiente()) {
  try { amb.localStorage.setItem(CHAVE_FAIXA_ADIADA, diaLocal(agoraMs)); return true; } catch { return false; }
}
